import React, {useState} from 'react';
import {
    Button,
    Confirm,
    useNotify,
    useUnselectAll,
} from 'react-admin';
import {assemblePostTasks} from '../utils/apiFunctions';
import {putOrdersStatus} from '../utils/apiOrderStatus';
import {nonDismissable} from '../utils/dialogFunctions';
import {isManualOrderStatus, orderStatusName} from '../global/orderStatus';

// What each bulk edit does: the button label, the end of the confirm
// question, whether it is ready to send, the save, and the error to show
const EDIT_ACTIONS = {
    bulk_edit_option_billed: ({selectedIds, selectedBilled}) => ({
        btnText: 'Set Billed State',
        content: `as ${(selectedBilled) ? 'Billed' : 'Not Billed'}?`,
        ready: true,
        save: () => assemblePostTasks({
            isOrderValueUpdate: true,
            isProductValueUpdate: false,
            selectedIds: selectedIds,
            updateType: 'billed',
            updateValue: selectedBilled
        }, () => {
        }),
        failure: () => 'Error: Orders not updated'
    }),
    // One request for the whole selection, which the API applies to all of
    // the orders or none (parrot-media/portal/main#322), so a failure leaves
    // nothing half done and its reason is worth showing
    bulk_edit_option_status: ({selectedIds, selectedStatus}) => ({
        btnText: 'Set Order Status',
        content: `to ${orderStatusName(selectedStatus)}?`,
        ready: isManualOrderStatus(selectedStatus),
        save: () => putOrdersStatus(selectedIds, selectedStatus),
        failure: (error) => `Error: Orders not updated: ${error.message}`
    })
};

const BulkEditOrderSubmitButton = ({
                                         editOption,
                                         selectedIds,
                                         selectedBilled,
                                         selectedStatus,
                                         onSuccess
                                     }) => {
    const [open, setOpen] = useState(false);
    // A second click on Confirm while the first save is running is ignored
    const [saving, setSaving] = useState(false);
    const notify = useNotify();
    const unselectAll = useUnselectAll();

    const action = EDIT_ACTIONS[editOption]?.({selectedIds, selectedBilled, selectedStatus});

    const saveData = async () => {
        if (!action) {
            return;
        }
        return await action.save()
            .then(() => {
                notify('Orders updated');
                if (typeof onSuccess === 'function') {
                    onSuccess();
                    unselectAll('Order');
                }
            })
            .catch(error => {
                console.log('Posting Error: ', error);
                notify(action.failure(error), 'warning');
            });
    };

    const handleClick = () => setOpen(true);

    const handleDialogClose = () => {
        setOpen(false);
    };

    const handleConfirm = () => {
        if (saving) {
            return;
        }
        setSaving(true);
        saveData().then(() => {
            setSaving(false);
            setOpen(false);
        });
    };

    // Assemble button and confirmation text
    const productStr = (selectedIds.length === 1) ? 'Order' : 'Orders';

    return (
        <>
            {editOption &&
            <Button
                color="primary"
                variant="outlined"
                size="medium"
                label={action?.btnText ?? 'Update Orders'}
                onClick={handleClick}
                disabled={!action?.ready}
                className="btn-bulk-edit"
            />
            }
            <Confirm
                isOpen={open}
                loading={saving}
                title="Update Order"
                content={`Are you sure you want to update ${selectedIds.length} ${productStr} ${action?.content ?? '?'}`}
                onConfirm={handleConfirm}
                onClose={nonDismissable(handleDialogClose)}
            />
        </>
    );
}

export default BulkEditOrderSubmitButton;
